/* JP - Banner video (TNRS-195)
 *
 * <jp-banner-video> progressively enhances the background videos in the hero
 * banner sections with the playback controls the merchant enabled in the
 * theme editor: a play/pause toggle, a sound toggle, or a full control bar.
 *
 * The element is an overlay stretched over the slide, not a wrapper, so it
 * never sits between the banner link and its content. It finds the videos on
 * the closest `.jp-banner-video-host` (the slide), which is also why hover
 * styling hangs off that host rather than off this element — the overlay is
 * click-through so it is not a hover target itself.
 *
 * A banner renders up to two videos, desktop and mobile, and hides one of them
 * with `display: none` at any given breakpoint. A hidden video still plays and
 * is still audible, so the two must never run together: this element keeps the
 * desired state (`soundOn` / `playRequested`) and applies it to the on-screen
 * video only, force-muting and pausing every other one. Crossing the 1000px
 * breakpoint hands that state over to whichever video is now on screen.
 *
 * Autoplaying video always starts muted: browsers block autoplay with sound.
 * The sound button is what lets a visitor opt into audio afterwards. A
 * click-to-play video can opt into sound on its first play instead
 * (`data-sound-on-play`), since that click is the gesture browsers require.
 */
if (!customElements.get("jp-banner-video")) {
  /* Matches the `hidden-pocket` / `hidden-lap-and-up` switch in theme.css. */
  const DESKTOP_BREAKPOINT = "(min-width: 1000px)";

  class JPBannerVideo extends HTMLElement {
    connectedCallback() {
      this.host = this.closest(".jp-banner-video-host") || this.parentElement;
      this.videos = Array.prototype.slice.call(
        this.host ? this.host.querySelectorAll("video") : [],
      );
      if (!this.videos.length) return;

      this.autoplay = this.dataset.autoplay === "true";
      this.soundOnPlay = this.dataset.soundOnPlay === "true";
      this.playToggles = this.querySelectorAll("[data-jp-video-play]");
      this.soundToggles = this.querySelectorAll("[data-jp-video-sound]");
      this.seek = this.querySelector("[data-jp-video-seek]");
      this.currentTimeEl = this.querySelector("[data-jp-video-current-time]");
      this.durationEl = this.querySelector("[data-jp-video-duration]");

      // The state the visitor asked for, as opposed to what any one <video>
      // element happens to be doing. Everything starts muted; only the video
      // on screen is ever allowed to play or to carry sound.
      this.soundOn = false;
      this.playRequested = this.autoplay && !this._prefersReducedMotion();

      // The visitor is dragging the seek slider — leave the displayed
      // position alone until they let go, or it fights their input.
      this.scrubbing = false;

      this._onPlayToggle = this._onPlayToggle.bind(this);
      this._onSoundToggle = this._onSoundToggle.bind(this);
      this._onBreakpointChange = this._onBreakpointChange.bind(this);
      this._syncPlaybackState = this._syncPlaybackState.bind(this);
      this._syncSoundState = this._syncSoundState.bind(this);
      this._syncProgress = this._syncProgress.bind(this);

      this.playToggles.forEach((button) =>
        button.addEventListener("click", this._onPlayToggle),
      );
      this.soundToggles.forEach((button) =>
        button.addEventListener("click", this._onSoundToggle),
      );

      this.videos.forEach((video) => {
        video.addEventListener("play", this._syncPlaybackState);
        video.addEventListener("pause", this._syncPlaybackState);
        video.addEventListener("ended", this._syncPlaybackState);
        video.addEventListener("volumechange", this._syncSoundState);
        video.addEventListener("timeupdate", this._syncProgress);
        video.addEventListener("loadedmetadata", this._syncProgress);
      });

      if (this.seek) {
        this.seek.addEventListener("input", (event) => {
          this.scrubbing = true;
          this._seekTo(parseFloat(event.target.value));
        });
        this.seek.addEventListener("change", (event) => {
          this._seekTo(parseFloat(event.target.value));
          this.scrubbing = false;
        });
      }

      this.breakpoint =
        typeof window.matchMedia === "function"
          ? window.matchMedia(DESKTOP_BREAKPOINT)
          : null;
      if (this.breakpoint) {
        // Safari below 14 only has the deprecated addListener.
        if (this.breakpoint.addEventListener) {
          this.breakpoint.addEventListener("change", this._onBreakpointChange);
        } else {
          this.breakpoint.addListener(this._onBreakpointChange);
        }
      }

      this._applyState();
      this._syncPlaybackState();
      this._syncSoundState();
      this._syncProgress();
    }

    disconnectedCallback() {
      if (!this.videos || !this.videos.length) return;

      this.playToggles.forEach((button) =>
        button.removeEventListener("click", this._onPlayToggle),
      );
      this.soundToggles.forEach((button) =>
        button.removeEventListener("click", this._onSoundToggle),
      );
      this.videos.forEach((video) => {
        video.removeEventListener("play", this._syncPlaybackState);
        video.removeEventListener("pause", this._syncPlaybackState);
        video.removeEventListener("ended", this._syncPlaybackState);
        video.removeEventListener("volumechange", this._syncSoundState);
        video.removeEventListener("timeupdate", this._syncProgress);
        video.removeEventListener("loadedmetadata", this._syncProgress);

        // A detached <video> can keep playing until it is collected. The theme
        // editor re-renders the whole section on every setting change, so
        // without this the outgoing copy is still audible under the new one.
        video.muted = true;
        video.pause();
      });

      if (this.breakpoint) {
        if (this.breakpoint.removeEventListener) {
          this.breakpoint.removeEventListener(
            "change",
            this._onBreakpointChange,
          );
        } else {
          this.breakpoint.removeListener(this._onBreakpointChange);
        }
      }
    }

    /* The video currently on screen. Desktop and mobile variants are toggled
       with `hidden-pocket` / `hidden-lap-and-up`, so the hidden one has no
       layout box. */
    get activeVideo() {
      const visible = this.videos.filter(
        (video) => video.getClientRects().length > 0,
      );
      return visible[0] || this.videos[0];
    }

    /* Single writer for every <video> in the banner. The on-screen one follows
       the requested state; the rest are silenced and stopped, which is what
       keeps a hidden mobile video from playing its audio under the desktop
       one. */
    _applyState() {
      const active = this.activeVideo;

      this.videos.forEach((video) => {
        if (video !== active) {
          video.muted = true;
          // Also drop the attribute: an element paused before its data
          // arrives would otherwise still honour autoplay once it loads.
          video.removeAttribute("autoplay");
          if (!video.paused) video.pause();
          return;
        }

        video.muted = !this.soundOn;

        if (this.playRequested && video.paused) {
          this._play(video);
        } else if (!this.playRequested && !video.paused) {
          video.removeAttribute("autoplay");
          video.pause();
        }
      });
    }

    _play(video) {
      const playback = video.play();
      if (playback && typeof playback.catch === "function") {
        // Autoplay can still be refused (a video with sound on, say). Fall
        // back to the paused state rather than showing a pause button for a
        // video that is not running.
        playback.catch(() => {
          this.playRequested = false;
          this._syncPlaybackState();
        });
      }
    }

    _prefersReducedMotion() {
      // WCAG 2.2.2: visitors who asked their OS to reduce motion get a still
      // frame and a play button rather than a looping video.
      return (
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      );
    }

    _onBreakpointChange() {
      // The other video is on screen now; hand the current state over to it.
      this._applyState();
      this._syncPlaybackState();
      this._syncSoundState();
      this._syncProgress();
    }

    _onPlayToggle(event) {
      // The banner can be wrapped in a whole-banner link; keep a control click
      // from also following it.
      event.preventDefault();
      event.stopPropagation();

      this.playRequested = this.activeVideo.paused;

      // Only the first play turns the sound on. After that the sound button
      // decides, so a visitor who muted and paused is not unmuted on resume.
      if (this.playRequested && this.soundOnPlay) {
        this.soundOn = true;
        this.soundOnPlay = false;
      }

      this._applyState();
    }

    _onSoundToggle(event) {
      event.preventDefault();
      event.stopPropagation();

      this.soundOn = !this.soundOn;
      // The visitor has made their own choice; a later play must not undo it.
      this.soundOnPlay = false;

      // Turning sound on for a video the visitor never started should start
      // it — otherwise the button appears to do nothing.
      if (this.soundOn && this.activeVideo.paused) this.playRequested = true;

      this._applyState();
    }

    _seekTo(percentage) {
      const video = this.activeVideo;
      if (!isFinite(video.duration) || isNaN(percentage)) return;

      video.currentTime = (percentage / 100) * video.duration;
    }

    _syncPlaybackState() {
      const playing = !this.activeVideo.paused && !this.activeVideo.ended;

      this.classList.toggle("jp-banner-video--playing", playing);
      this.classList.toggle("jp-banner-video--paused", !playing);

      // A poster image covers the video only until it first plays; pausing
      // afterwards leaves the current frame showing, as a native poster does.
      if (playing && this.host) {
        this.host.classList.add("jp-banner-video-host--started");
      }
      this.playToggles.forEach((button) => this._syncToggle(button, playing));
    }

    _syncSoundState() {
      const muted = this.activeVideo.muted;

      this.classList.toggle("jp-banner-video--muted", muted);
      this.soundToggles.forEach((button) => this._syncToggle(button, !muted));
    }

    /* Each toggle holds two icons and two labels; `active` picks which of the
       pair is shown and announced. The icons are <svg> elements, which have no
       `hidden` IDL attribute of their own, so the attribute is set by hand. */
    _syncToggle(button, active) {
      const activeIcon = button.querySelector("[data-jp-video-icon-on]");
      const inactiveIcon = button.querySelector("[data-jp-video-icon-off]");
      if (activeIcon) this._setHidden(activeIcon, !active);
      if (inactiveIcon) this._setHidden(inactiveIcon, active);

      const label = active ? button.dataset.labelOn : button.dataset.labelOff;
      if (label) {
        button.setAttribute("aria-label", label);
        button.setAttribute("title", label);
      }
    }

    _setHidden(element, hidden) {
      if (hidden) element.setAttribute("hidden", "");
      else element.removeAttribute("hidden");
    }

    _syncProgress() {
      if (!this.seek && !this.currentTimeEl && !this.durationEl) return;

      const video = this.activeVideo;
      const duration = isFinite(video.duration) ? video.duration : 0;

      if (this.seek && !this.scrubbing) {
        this.seek.value = duration
          ? String((video.currentTime / duration) * 100)
          : "0";
      }
      if (this.currentTimeEl) {
        this.currentTimeEl.textContent = this._formatTime(video.currentTime);
      }
      if (this.durationEl) {
        this.durationEl.textContent = this._formatTime(duration);
      }
    }

    _formatTime(seconds) {
      if (!isFinite(seconds) || seconds < 0) seconds = 0;

      const minutes = Math.floor(seconds / 60);
      const remainder = Math.floor(seconds % 60);
      return minutes + ":" + (remainder < 10 ? "0" : "") + remainder;
    }
  }

  customElements.define("jp-banner-video", JPBannerVideo);
}
